import { makeAutoObservable } from 'mobx'
import { uiStore } from 'shared/store/uiStore'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import { VoiceServicesApi } from 'entities/Compliance/api/voiceServicesApi'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { SettingsPath } from 'pages/settings/route/settingsPath'
import { CallingRoutesSteps } from 'pages/settings/pages/compliance/pages/calling/route/type'
import { CommonNumbersStore } from 'pages/settings/pages/compliance/ui/CommonNumbers/store/commonNumbersStore'
import { type CallingStepsStore } from './callingStepsStore'

export class CnamCallerIdStore {
  private _callerName = ''
  private _isCallerNameValid = true
  private _isConfigureTermsChecked = false
  private readonly _numbersStore: CommonNumbersStore
  private _loading = false
  private _errorMessage: string | null = null
  private _preassignedData = false

  constructor(private _callingStepsStore: CallingStepsStore, private _entities: IEntitiesStore) {
    this._numbersStore = new CommonNumbersStore(
      {
        setLoading: this.setLoading,
      },
      this._entities
    )

    makeAutoObservable(this)

    this.init()
  }

  get submitDisabled() {
    return (
      !this._isConfigureTermsChecked ||
      !this.numbersStore.multiSelectedNumbers.length ||
      this.loading ||
      !this._callerName.length
    )
  }

  get callerName() {
    return this._callerName
  }

  get callingStepsStore() {
    return this._callingStepsStore
  }

  get numbersStore() {
    return this._numbersStore
  }

  get isConfigureTermsChecked() {
    return this._isConfigureTermsChecked
  }

  get loading() {
    return this._loading
  }

  get freeNumbers() {
    return this._entities.complianceStore.complianceVoiceServicesStore.freeNumbers
  }

  get unassignedFreeNumbers() {
    return this.freeNumbers.filter((number) => !number.numberable?.name)
  }

  get assignedFreeNumbers() {
    return this.freeNumbers.filter((number) => number.numberable?.name)
  }

  get errorMessage() {
    return this._errorMessage
  }

  get isCallerNameValid() {
    return this._isCallerNameValid
  }

  init = async () => {
    this.setLoading(true)
    await this._entities.complianceStore.complianceVoiceServicesStore.loadFreeNumbers()
    this.setLoading(false)

    this.initNumbersStoreForAdding()
  }

  setLoading = (value: boolean) => {
    this._loading = value
  }

  initNumbersStoreForAdding = () => {
    this.numbersStore.init({
      unassignedNumbers: this.unassignedFreeNumbers,
      assignedNumbers: this.assignedFreeNumbers,
    })
  }

  setCallerName = (value: string) => {
    this._isCallerNameValid = true
    this._callerName = value.slice(0, 15)
  }

  setIsConfigureTermsChecked = (value: boolean) => {
    this._isConfigureTermsChecked = value
  }

  setPreassignedData = async (id: number) => {
    const cnam =
      this._entities.complianceStore.complianceVoiceServicesStore.cnamServices?.getItem(id)

    if (cnam && !this._preassignedData) {
      this.setLoading(true)

      const numbers = await this._entities.complianceStore.loadCnamNumbers(id)

      this.setLoading(false)
      this._callerName = cnam.name
      this._errorMessage = cnam.registrationError?.message || null

      numbers?.forEach((number) => {
        this.numbersStore.setMultipleItem({
          id: number.id.toString(),
          label: number.number,
          value: number.id.toString(),
          data: number,
        })
      })

      this._preassignedData = true
    }
  }

  moveToSecondStep = (id: number | null) => {
    this.callingStepsStore.stepsStore.setStep(2)

    uiStore.changeRoute({
      path: `/${SettingsPath.compliance.calling.root}/${id ? `${id}/` : ''}${
        CallingRoutesSteps.configure
      }`,
    })
  }

  checkCallerName = () => {
    const regex = /^[A-Za-z][A-Za-z0-9., ]{0,14}$/

    this._isCallerNameValid = regex.test(this._callerName)
  }

  handleSubmit = async (id: number | null) => {
    this.checkCallerName()

    if (
      !this.isCallerNameValid ||
      this.callingStepsStore.complianceLayerStore.preventSubmitModal()
    ) {
      return
    }

    try {
      this.setLoading(true)

      let response

      if (id) {
        response = await VoiceServicesApi.updateCnamCallerId(
          {
            name: this.callerName,
            phone_numbers: this.numbersStore.multiSelectedKeys,
          },
          id
        )
      } else {
        response = await VoiceServicesApi.addCnamCallerId({
          name: this.callerName,
          phone_numbers: this.numbersStore.multiSelectedKeys,
        })
      }

      setTimeout(() => {
        // TODO: remove this after adding socket
        this._entities.complianceStore.complianceVoiceServicesStore.fetchComplianceVoiceServicesList()
      }, 2000)

      if (response?.data) {
        this._entities.complianceStore.complianceVoiceServicesStore.cnamServices?.syncData([
          response.data,
        ])

        uiStore.changeRoute({
          path: `/${SettingsPath.compliance.calling.root}`,
        })

        toastStore.add({
          title: 'CNAM Caller ID submitted for review',
          type: 'info',
        })
      }
    } catch (error) {
      logger.error(error)
    } finally {
      this.setLoading(false)
    }
  }
}
